import { request } from '@/lib/api';

/** What a ledger represents, matching the API's LedgerKind enum. */
export const LedgerKind = {
  general: 1,
  cash: 2,
  bank: 3,
  customer: 4,
  supplier: 5,
  employee: 6,
  tax: 7,
  additionalCharge: 8,
} as const;

/** A ledger as a lookup shows it. */
export interface LedgerSummary {
  readonly ledgerId: string;
  readonly code: string;
  readonly name: string;
  readonly kind: number;
  readonly groupCode: string;
  readonly groupName: string;
  readonly nature: number;
  readonly currency: string;
  readonly isBillWise: boolean;
  /** The Arabic name, where the ledger has one. */
  readonly nameArabic?: string | null;
}

/**
 * Lists the firm's ledgers.
 *
 * Unpaged, which is the API's own decision and the right one: a chart of accounts is
 * a few hundred rows, every caller is a picker being typed into, and a round trip per
 * keystroke is what makes a picker feel broken.
 */
export function listLedgers(activeOnly = true): Promise<readonly LedgerSummary[]> {
  return request<readonly LedgerSummary[]>(
    `/accounting/ledgers?activeOnly=${String(activeOnly)}`,
  );
}

/**
 * A party a payment or a receipt can be against.
 *
 * Customers, suppliers and employees — the three kinds of ledger somebody hands
 * money to or takes it from. A general ledger is not one: paying rent is a payment
 * against an expense account, and the screen offers that separately rather than
 * calling the landlord a party.
 */
export function isParty(ledger: LedgerSummary): boolean {
  return (
    ledger.kind === LedgerKind.customer ||
    ledger.kind === LedgerKind.supplier ||
    ledger.kind === LedgerKind.employee
  );
}

/** Whether a ledger is one of the firm's own cash or bank accounts. */
export function isMoneyAccount(ledger: LedgerSummary): boolean {
  return ledger.kind === LedgerKind.cash || ledger.kind === LedgerKind.bank;
}

/**
 * The codes the standard chart seeds for the charges a document may carry.
 *
 * Read by code because the API exposes no endpoint for the charge matrix — the
 * mapping of ledger to document type lives on the server and is checked there, so a
 * charge this list offers can still be refused, and the server's message says so.
 * Offering the five the seeding creates is what makes freight and a discount
 * reachable at all; without it a purchase could carry neither.
 */
export const CHARGE_LEDGER_CODES: readonly string[] = [
  'FREIGHT',
  'PACKING',
  'DELIVERY',
  'DISC-ALLOWED',
  'ROUND-OFF',
];

/**
 * The ledgers a document's charges may post to.
 *
 * Anything the firm has classified as an additional charge, plus the seeded codes
 * for firms whose charge accounts were created before that classification existed.
 */
export function chargeLedgers(
  ledgers: readonly LedgerSummary[],
): readonly LedgerSummary[] {
  return ledgers.filter(
    (ledger) =>
      ledger.kind === LedgerKind.additionalCharge ||
      CHARGE_LEDGER_CODES.includes(ledger.code.toUpperCase()),
  );
}

/**
 * Whether a charge adds to a document's total or comes off it.
 *
 * The server holds the real answer on the charge matrix, where it is a flag set per
 * ledger per document type — freight a firm pays is a cost, freight it recovers is
 * income. This is the screen's guess for the running total it shows while somebody
 * types, and it is only ever a guess: the figure that reaches the books is the one
 * the server computes.
 */
export function chargeAdds(ledger: LedgerSummary): boolean {
  return !ledger.code.toUpperCase().startsWith('DISC');
}

/** Standard 5 Primary Account Natures / Heads. */
export const AccountNature = {
  asset: 1,
  liability: 2,
  equity: 3,
  income: 4,
  expense: 5,
} as const;

export type AccountNatureValue = (typeof AccountNature)[keyof typeof AccountNature];

export interface AccountHeadMeta {
  readonly key: string;
  readonly name: string;
  readonly nameArabic: string;
  readonly order: number;
  readonly tone: 'asset' | 'liability' | 'equity' | 'income' | 'expense';
}

export const ACCOUNT_HEAD_META: Record<number, AccountHeadMeta> = {
  1: { key: 'Asset', name: 'Assets', nameArabic: 'الأصول', order: 1, tone: 'asset' },
  2: {
    key: 'Liability',
    name: 'Liabilities',
    nameArabic: 'الالتزامات',
    order: 2,
    tone: 'liability',
  },
  3: {
    key: 'Equity',
    name: 'Equity',
    nameArabic: 'حقوق الملكية',
    order: 3,
    tone: 'equity',
  },
  4: {
    key: 'Income',
    name: 'Income / Revenue',
    nameArabic: 'الإيرادات',
    order: 4,
    tone: 'income',
  },
  5: {
    key: 'Expense',
    name: 'Expenses',
    nameArabic: 'المصروفات',
    order: 5,
    tone: 'expense',
  },
};

export interface LedgerStatementLine {
  readonly date: string;
  readonly voucherId: string;
  readonly voucherNumber: string;
  readonly voucherType: number | string;
  readonly referenceNumber: string | null;
  readonly narration: string | null;
  readonly contraLedgerNames: readonly string[];
  readonly debit: number;
  readonly credit: number;
  readonly runningBalance: number;
}

export interface LedgerStatementResponse {
  readonly ledgerId: string;
  readonly ledgerCode: string;
  readonly ledgerName: string;
  readonly groupName: string;
  readonly currency: string;
  readonly from: string;
  readonly to: string;
  readonly openingBalance: number;
  readonly closingBalance: number;
  readonly totalDebit: number;
  readonly totalCredit: number;
  readonly lines: readonly LedgerStatementLine[];
}

export function fetchLedgerStatement(
  ledgerId: string,
  from: string,
  to: string,
): Promise<LedgerStatementResponse> {
  const params = new URLSearchParams({ from, to });
  return request<LedgerStatementResponse>(
    `/accounting/reports/ledger-statement/${ledgerId}?${params.toString()}`,
  );
}

/** The kinds the chart of accounts creates and edits; the others have screens of their own. */
export const PLAIN_KINDS = [
  LedgerKind.general,
  LedgerKind.cash,
  LedgerKind.bank,
  LedgerKind.employee,
] as const;

/** Whether the chart of accounts may edit a ledger of this kind. */
export function isPlainKind(kind: number): boolean {
  return (PLAIN_KINDS as readonly number[]).includes(kind);
}

/** Which side an opening balance falls on, matching the API's EntrySide enum. */
export const EntrySide = { debit: 1, credit: 2 } as const;

/** A ledger with everything its form shows. */
export interface LedgerDetail {
  readonly ledgerId: string;
  readonly code: string;
  readonly name: string;
  readonly nameArabic: string | null;
  readonly accountGroupId: string;
  readonly groupName: string;
  readonly nature: number;
  readonly kind: number;
  readonly currency: string;
  readonly openingBalance: number;
  readonly openingSide: number;
  readonly phone: string | null;
  readonly email: string | null;
  readonly address: string | null;
  readonly isActive: boolean;
  readonly isEditable: boolean;
}

export interface SaveLedger {
  readonly code?: string | null;
  readonly name: string;
  readonly nameArabic: string | null;
  readonly accountGroupId: string;
  readonly kind: number;
  readonly openingBalance: number;
  readonly openingSide: number;
  readonly phone: string | null;
  readonly email: string | null;
  readonly address: string | null;
}

/** An account group, a heading ledgers sit under. */
export interface AccountGroupSummary {
  readonly accountGroupId: string;
  readonly code: string;
  readonly name: string;
  readonly nameArabic: string | null;
  readonly nature: number;
  readonly parentId: string | null;
  readonly isSystemGroup: boolean;
}

const LEDGERS = '/accounting/ledgers';
const GROUPS = '/accounting/account-groups';

export const getLedger = (id: string): Promise<LedgerDetail> =>
  request<LedgerDetail>(`${LEDGERS}/${id}`);

export const createLedger = (body: SaveLedger): Promise<LedgerDetail> =>
  request<LedgerDetail>(LEDGERS, { method: 'POST', body });

export const updateLedger = (id: string, body: SaveLedger): Promise<LedgerDetail> =>
  request<LedgerDetail>(`${LEDGERS}/${id}`, { method: 'PUT', body });

export const setLedgerActive = (id: string, isActive: boolean): Promise<LedgerDetail> =>
  request<LedgerDetail>(`${LEDGERS}/${id}/active`, {
    method: 'POST',
    body: { isActive },
  });

export const listAccountGroups = (): Promise<readonly AccountGroupSummary[]> =>
  request<readonly AccountGroupSummary[]>(GROUPS);

export const createAccountGroup = (body: {
  readonly code?: string | null;
  readonly name: string;
  readonly nameArabic: string | null;
  readonly parentId: string;
}): Promise<AccountGroupSummary> =>
  request<AccountGroupSummary>(GROUPS, { method: 'POST', body });

export const renameAccountGroup = (
  id: string,
  body: { readonly name: string; readonly nameArabic: string | null },
): Promise<AccountGroupSummary> =>
  request<AccountGroupSummary>(`${GROUPS}/${id}`, { method: 'PUT', body });

/** Groups as "Assets › Current Assets › Cash and Bank", for a picker. */
export function groupPaths(
  groups: readonly AccountGroupSummary[],
): ReadonlyMap<string, string> {
  const byId = new Map(groups.map((group) => [group.accountGroupId, group]));
  const paths = new Map<string, string>();
  for (const group of groups) {
    const names: string[] = [];
    let current: AccountGroupSummary | undefined = group;
    let guard = 0;
    while (current && guard++ < 10) {
      names.unshift(current.name);
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    paths.set(group.accountGroupId, names.join(' › '));
  }
  return paths;
}

/**
 * The groups whose ledgers money can be paid from or received into, beside the cash
 * and bank kinds: Current Assets and Cash and Bank. EasyRetail offered the same - its
 * schedule 7 for cash and 19 for banks, which the import files under these two.
 */
const MONEY_GROUPS: ReadonlySet<string> = new Set(['1100', '1110']);

/** Whether a ledger is one money can be paid from or received into. */
export function isCashOrBank(ledger: LedgerSummary): boolean {
  return (
    ledger.kind === LedgerKind.cash ||
    ledger.kind === LedgerKind.bank ||
    (MONEY_GROUPS.has(ledger.groupCode) && !isParty(ledger))
  );
}
